import { useEffect, useState } from 'react';
import { useDocumentTitle, useSearchParams } from '@shared/hooks';
import { CopyToClipboardButton, StatusLabel, TableActionButtons } from '@shared/components';
import { TGetArchivedItemsResult } from '../types/archive.types';
import { useArchivedItems } from '../api/archive.api';
import { useMemo } from 'react';
import { usePersistedColumnSizing } from '@shared/hooks';
import { ItemsTable, TItem } from '@modules/items';
import { createTableColumn, Subtitle1, useTableFeatures } from '@fluentui/react-components';
import { TTableRow } from '@shared/types';
import { Pagination } from '@shared/components';
import { calculateRowsPerPage } from '@shared/helpers';
import { ARCHIVE_TABLE_ID, ARCHIVE_UI_MARGIN_TOP } from '../constants';

const ArchivePage = () => {
  const { searchParams, change } = useSearchParams();
  const page = Number(searchParams.get('page'));
  const pageSize = Number(searchParams.get('pageSize'));

  useEffect(() => {
    if (Number(page) < 1) change('page', '1');
    if (Number(pageSize) < 1) change('pageSize', calculateRowsPerPage(ARCHIVE_UI_MARGIN_TOP));
  }, [page, pageSize]);

  const { data: apiData, error } = useArchivedItems({ page, pageSize });
  const [data, setData] = useState<TGetArchivedItemsResult | undefined>();

  useEffect(() => {
    if (error) {
      console.error('Error fetching archived items:', error);
      setData(undefined);
      return;
    }
    if (!apiData) return;
    setData(apiData);
  }, [apiData, error]);

  useDocumentTitle('Archive');

  return (
    <StatusLabel
      data={data}
      loadingMessage="Loading archived items..."
      errorMessage="Loading archived items failed!"
      render={(resolvedData) => (
        <ArchiveContent
          items={resolvedData.items}
          total={resolvedData.total}
          resolvedNames={resolvedData.resolvedNames}
        />
      )}
    />
  );
};

const columnIds = ['name', 'location', 'archived', 'latestVersionId', 'archivedBy'] as const;

const columnSizingParams = {
  autoFitColumns: false,
  columnSizingOptions: {
    name: { idealWidth: 300, minWidth: 150 },
    location: { idealWidth: 200, minWidth: 100 },
    archived: { defaultWidth: 125 },
    latestVersionId: { defaultWidth: 100 },
    archivedBy: { defaultWidth: 100 },
  },
};

const ArchiveContent = ({ items, total, resolvedNames }: TGetArchivedItemsResult) => {
  const columns = useMemo(() => columnIds.map((columnId) => createTableColumn<TItem>({ columnId })), []);

  const { columnSizing_unstable, getRows } = useTableFeatures({ columns, items, getRowId: (item) => item._id }, [
    usePersistedColumnSizing(ARCHIVE_TABLE_ID, columnSizingParams),
  ]);

  const rows: TTableRow<TItem>[] = getRows((row) => ({
    ...row,
    key: row.rowId as string,
    actionButton: (
      <TableActionButtons alwaysVisible>
        <CopyToClipboardButton value={row.rowId as string} label="Item ID" />
      </TableActionButtons>
    ),
  }));

  return (
    <div className="w-full max-w-screen-2xl pr-8">
      <p className="pb-4">
        <Subtitle1>Archive</Subtitle1>
      </p>
      {rows.length ? (
        <>
          <ItemsTable
            rows={rows}
            resolvedNames={resolvedNames}
            show={{
              location: true,
              status: false,
              date: true,
              archivedBy: true,
              authors: false,
              approvedVersion: false,
            }}
            dateType="archived"
            columnSizing={columnSizing_unstable}
            disable={{ name: true, location: true }}
          />
          <Pagination total={total} margin={ARCHIVE_UI_MARGIN_TOP} />
        </>
      ) : (
        <p>No archived items found!</p>
      )}
    </div>
  );
};

export default ArchivePage;
